import { useState, useEffect, useMemo } from "react";
import Txn from "./Txn";
import { transactionAPI } from "services";
import useUniversalStore from "store/universalStore";
import {
  OpenCloseBalance,
  ListHeader,
  ShimmerLoader
} from "components";
import { formatMoney, debounce } from "utils";

const TxnsListView = ({ monthlyDate, animation, setAnimation, hideClosed }) => {
  const txns = useUniversalStore((state) => state.transactions) || [];
  const { accountId, isReminderChanged, write } = useUniversalStore((state) => state);
  const [loading, setLoading] = useState(false);
  const [openBalance, setOpenBalance] = useState(0);
  const [loader, setLoader] = useState(false);

  // Filter transactions based on hideClosed toggle
  const filteredTnx = useMemo(() => {
    if (hideClosed) {
      return txns.filter((item) => !item.get("cancelled"));
    }
    return txns;
  }, [hideClosed, txns]);


  const monthlyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    txns.forEach(txn => {
      if (!txn.get("cancelled")) {
        const amount = txn.get("amount") || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalInOutChange: cashIn - cashOut
    };
  }, [txns]);


  const closeBalance = useMemo(() => {
    return openBalance + monthlyCash.totalInOutChange;
  }, [openBalance, monthlyCash.totalInOutChange]);

  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (mDate, accId, isRemChanged) => {
      if (!accId || !mDate) return;
      try {
        const startDate = new Date(mDate.getFullYear(), mDate.getMonth(), 1, 0, 0, 0, 0);
        const prevMonthEnd = new Date(startDate.getTime() - 1);

        const [txnsResult, balanceResult] = await Promise.all([
          transactionAPI.getTransactionsByAccountIdWithMonth(accId, mDate),
          transactionAPI.sumTillDateByAccount(prevMonthEnd, accId)
        ]);

        write({ transactions: txnsResult });
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Khata data:", error);
      } finally {
        setLoading(false);
      }
    };

    return debounce(fetchFunc, 800);
  }, []);

  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(monthlyDate, accountId, isReminderChanged);
  }, [monthlyDate, accountId, isReminderChanged]);

  return (
    <div
      className={`background mb-auto pb-5 slide position-relative ${animation} animate__slideInRight`}
      onAnimationEnd={() => setAnimation("")}>
      {loading ?
        <div className="row">
          <div className="col-md-6 list-group list-group-flush scrollbar-design h-100">
            {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
          </div>
          <div className="col-md-6 list-group list-group-flush scrollbar-design h-100">
            {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
          </div>
        </div>
        : <div className="row">
          <div className="col-md-6 list-group list-group-flush overflow-scroll scrollbar-design h-100"
            style={{ height: 'calc(100vh - 250px)', maxHeight: 'calc(100vh - 250px)' }}
          >
            <ListHeader type="in" amount={monthlyCash.in} />
            <OpenCloseBalance type="open" sum={openBalance} />
            {filteredTnx
              ?.filter((txn) => txn.get("amount") > 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  setLoader={setLoader}
                  monthlyDate={monthlyDate}
                />
              ))}
          </div>
          <div className="col-md-6 list-group list-group-flush overflow-scroll scrollbar-design h-100"
            style={{ height: 'calc(100vh - 250px)', maxHeight: 'calc(100vh - 250px)' }}>
            <ListHeader type="out" amount={monthlyCash.out} />
            {filteredTnx
              ?.filter((txn) => txn.get("amount") < 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  setLoader={setLoader}
                  monthlyDate={monthlyDate}
                />
              ))}
            <OpenCloseBalance type="close" sum={closeBalance} />
          </div>
        </div>}
    </div>
  );
};

export default TxnsListView;